import { NavLink, Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Sun, Tractor, Plant, CloudSun, CalendarCheck, Storefront,
  UsersThree, Newspaper, CaretDown, Package, Drop, Leaf, Cow, Bug,
  MapTrifold, Books, Sparkle, Lightbulb, Wrench, Bank, Warning,
  Notebook, Basket, Handshake, Fish, House, Compass,
  Barn, Bed, ForkKnife, Trophy, Truck, Warehouse, ChatCircleDots,
  GraduationCap, FileText, Coins, Confetti, IdentificationBadge,
  ShoppingBag, ChartLine, PottedPlant, Buildings, Camera, MapPin,
  ArrowRight, X,
} from "@phosphor-icons/react";

/**
 * FarmersNav — the shared sub-nav that appears on every /farmers/* page.
 *
 * Primary row (compact, always visible):
 *   Today · Network · My Farm · Crops · Weather · Calendar · Markets ·
 *   Products · Community · News · More ▾
 *
 * The More menu is organised into 6 logical sections (Farm Operations,
 * Sell & Earn, Visit & Experience, Knowledge, Support, Tools) so the
 * 40+ modules of Farmers Pulse never overwhelm the farmer.
 *
 * Personalisation: farmers can pin their favourite More entries to a
 * "Pinned" section at the top of the menu — persisted per-user in
 * localStorage under `np:farmers-pinned`.
 */

const PRIMARY = [
  { to: "/farmers/today",     label: "Today",     Icon: Sun },
  { to: "/farmers/network",   label: "Network",   Icon: UsersThree },
  { to: "/farmers/mine",      label: "My Farm",   Icon: Tractor },
  { to: "/farmers/crops",     label: "Crops",     Icon: Plant },
  { to: "/farmers/weather",   label: "Weather",   Icon: CloudSun },
  { to: "/farmers/calendar",  label: "Calendar",  Icon: CalendarCheck },
  { to: "/farmers/markets",   label: "Markets",   Icon: Storefront },
  { to: "/farmers/products",  label: "Products",  Icon: Basket },
  { to: "/farmers/community", label: "Community", Icon: ChatCircleDots },
  { to: "/farmers/news",      label: "News",      Icon: Newspaper },
];

/** Grouped "More" menu — organised the way the user asked. */
const MORE_GROUPS = [
  {
    heading: "Farm Operations",
    items: [
      { to: "/farmers/inventory",  label: "Inventory",   Icon: Package },
      { to: "/farmers/livestock",  label: "Livestock",   Icon: Cow },
      { to: "/farmers/soil",       label: "Soil",        Icon: Leaf },
      { to: "/farmers/water",      label: "Water",       Icon: Drop },
      { to: "/farmers/equipment",  label: "Equipment",   Icon: Wrench },
      { to: "/farmers/journal",    label: "Farm Journal", Icon: Notebook },
      { to: "/farmers/reports",    label: "Reports",     Icon: FileText },
    ],
  },
  {
    heading: "Sell & Earn",
    items: [
      { to: "/farmers/products",    label: "Products",    Icon: Basket },
      { to: "/farmers/deals",       label: "Deals",       Icon: Trophy },
      { to: "/farmers/surplus",     label: "Surplus",     Icon: Handshake },
      { to: "/farmers/marketplace", label: "Marketplace", Icon: ShoppingBag },
      { to: "/farmers/farm-stand",  label: "Farm Stand",  Icon: Storefront },
      { to: "/farmers/markets",     label: "Markets",     Icon: ChartLine },
      { to: "/farmers/buyers",      label: "Buyers",      Icon: Handshake },
      { to: "/farmers/needs",       label: "Needs",       Icon: Warning },
    ],
  },
  {
    heading: "Visit & Experience",
    items: [
      { to: "/farmers/farm-stay",     label: "Farm Stay",   Icon: Bed },
      { to: "/farmers/farm-dining",   label: "Farm Dining", Icon: ForkKnife },
      { to: "/farmers/experiences",   label: "Experiences", Icon: Confetti },
      { to: "/farmers/events",        label: "Events",      Icon: Confetti },
    ],
  },
  {
    heading: "Knowledge",
    items: [
      { to: "/farmers/almanac",    label: "Almanac",    Icon: Sun },
      { to: "/farmers/learning",   label: "Learning",   Icon: GraduationCap },
      { to: "/farmers/research",   label: "Research",   Icon: Lightbulb },
      { to: "/farmers/news",       label: "News",       Icon: Newspaper },
      { to: "/farmers/innovation", label: "Innovation", Icon: Sparkle },
    ],
  },
  {
    heading: "Support",
    items: [
      { to: "/farmers/government",   label: "Government",   Icon: Bank },
      { to: "/farmers/farm-help",    label: "Farm Help",    Icon: UsersThree },
      { to: "/farmers/jobs",         label: "Jobs",         Icon: IdentificationBadge },
      { to: "/farmers/services",     label: "Services",     Icon: Wrench },
      { to: "/farmers/cooperatives", label: "Cooperatives", Icon: UsersThree },
      { to: "/farmers/transport",    label: "Transport",    Icon: Truck },
      { to: "/farmers/storage",      label: "Storage",      Icon: Warehouse },
    ],
  },
  {
    heading: "Tools",
    items: [
      { to: "/farmers/websites",  label: "Websites",  Icon: Storefront },
      { to: "/farmers/alerts",    label: "Alerts",    Icon: Warning },
      { to: "/farmers/documents", label: "Documents", Icon: FileText },
      { to: "/farmers/finance",   label: "Finance",   Icon: Coins },
      { to: "/farmers/map",       label: "Farm Map",  Icon: MapTrifold },
      { to: "/farmers/local",     label: "Local Pulse", Icon: Compass },
      { to: "/farmers/geo-tree",  label: "Geographic Tree", Icon: MapPin },
      { to: "/farmers/pest-heatmap", label: "Pests & Diseases", Icon: Bug },
      { to: "/farmers/farms",     label: "Farm Registry", Icon: House },
      { to: "/farmers/observations", label: "Observations", Icon: Sparkle },
      { to: "/farmers/marketplace-events", label: "Farmers Market Events", Icon: Storefront },
      { to: "/farmers/knowledge", label: "Farmer Knowledge", Icon: Books },
      { to: "/farmers/ask",       label: "Ask Farmers AI", Icon: Sparkle },
      { to: "/farmers/report",    label: "Report What You See", Icon: Camera },
    ],
  },
];

const PINNED_KEY = "np:farmers-pinned";
function loadPinned() {
  try { return new Set(JSON.parse(localStorage.getItem(PINNED_KEY) || "[]")); }
  catch { return new Set(); }
}
function savePinned(set) {
  localStorage.setItem(PINNED_KEY, JSON.stringify(Array.from(set)));
}

const base = "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase transition whitespace-nowrap";
const inactive = "border-black/10 bg-white/70 text-[#3A3830] hover:border-[color:var(--np-brand,#B85F2A)] hover:text-[#1B4332]";
const active   = "border-[color:var(--np-brand,#B85F2A)] bg-[#1B4332] text-[#F5EBDC]";

const testidOf = (label) =>
  label.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function FarmersNav() {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);
  const [pinned, setPinned] = useState(loadPinned);

  useEffect(() => {
    if (!open) return;
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) {
      const MENU_W = 520;
      const margin = 8;
      // Prefer left-anchored under the button; if that would overflow the
      // right edge of the viewport, pin the menu 8px from the right instead.
      let left = rect.left;
      if (left + MENU_W > window.innerWidth - margin) {
        left = Math.max(margin, window.innerWidth - MENU_W - margin);
      }
      setMenuPos({ top: rect.bottom + 8, left });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const menu = document.querySelector('[data-testid="farmers-nav-more-menu"]');
      if (menu && menu.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const togglePin = (to, e) => {
    e.preventDefault(); e.stopPropagation();
    const next = new Set(pinned);
    if (next.has(to)) next.delete(to); else next.add(to);
    setPinned(next); savePinned(next);
  };

  // Flatten to find the {to,label,Icon} for each pinned item.
  const flat = MORE_GROUPS.flatMap((g) => g.items);
  const pinnedItems = Array.from(pinned).map((to) => flat.find((i) => i.to === to)).filter(Boolean);

  return (
    <nav
      data-testid="farmers-nav"
      className="sticky top-16 md:top-[74px] z-30 bg-[color:var(--np-bg,#F5EBDC)]/95 backdrop-blur border-b border-black/10"
    >
      <div className="max-w-[1400px] mx-auto px-3 md:px-8 py-3">
        {/* Horizontally scrollable on <md so 10 tabs + More don't wrap into 5 rows */}
        <div className="flex md:flex-wrap items-center gap-2 overflow-x-auto md:overflow-visible no-scrollbar -mx-3 px-3 md:mx-0 md:px-0">
          {PRIMARY.map(({ to, label, Icon, end }) => (
            <NavLink
              key={to} to={to} end={end}
              data-testid={`farmers-nav-${testidOf(label)}`}
              className={({ isActive }) => `${base} ${isActive ? active : inactive}`}
            >
              <Icon size={12} weight="fill" />
              {label}
            </NavLink>
          ))}
          <button
            ref={btnRef}
            onClick={() => setOpen((v) => !v)}
            data-testid="farmers-nav-more"
            className={`${base} ${open ? active : inactive}`}
            aria-expanded={open}
          >
            <CaretDown size={11} weight="bold" />
            More
          </button>
          {process.env.REACT_APP_FARMERSPULSE_URL && (
            <a
              href={process.env.REACT_APP_FARMERSPULSE_URL}
              target="_blank" rel="noopener noreferrer"
              data-testid="farmers-nav-farmerspulse-link"
              className={`${base} ml-auto border-[color:var(--np-brand,#B85F2A)]/40 bg-[color:var(--np-brand,#B85F2A)]/10 text-[color:var(--np-brand,#B85F2A)] hover:bg-[color:var(--np-brand,#B85F2A)]/20`}
            >
              farmerspulse.com ↗
            </a>
          )}
        </div>
      </div>

      {open && typeof document !== "undefined" && menuPos && createPortal(
        <div
          data-testid="farmers-nav-more-menu"
          style={{ position: "fixed", top: menuPos.top, left: menuPos.left }}
          className="w-[520px] max-w-[calc(100vw-16px)] rounded-2xl border border-black/15 bg-white shadow-xl z-[1000] p-3 max-h-[80vh] overflow-y-auto"
        >
          {pinnedItems.length > 0 && (
            <div className="mb-2 pb-2 border-b border-black/10">
              <div className="px-2 pb-1 font-mono-tech text-[9px] tracking-[0.32em] uppercase text-[#B85F2A]">
                <Sparkle size={9} weight="fill" className="inline mr-1" /> Pinned
              </div>
              <div className="grid grid-cols-2 gap-1">
                {pinnedItems.map(({ to, label, Icon }) => (
                  <MoreLink key={to} to={to} label={label} Icon={Icon} pinned
                            onPinToggle={togglePin} onClose={() => setOpen(false)} />
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-3 gap-y-3">
            {MORE_GROUPS.map((g) => (
              <div key={g.heading}>
                <div className="px-2 pb-1 font-mono-tech text-[9px] tracking-[0.32em] uppercase text-[#3A3830]">
                  {g.heading}
                </div>
                <div className="grid gap-0.5">
                  {g.items.map(({ to, label, Icon }) => (
                    <MoreLink key={to + label} to={to} label={label} Icon={Icon}
                              isPinned={pinned.has(to)}
                              onPinToggle={togglePin}
                              onClose={() => setOpen(false)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 pt-2 border-t border-black/10 text-[10px] font-mono-tech tracking-[0.24em] uppercase text-[#3A3830]">
            Tap the star to pin your favourites to the top.
          </div>
        </div>,
        document.body,
      )}
    </nav>
  );
}

function MoreLink({ to, label, Icon, pinned, isPinned, onPinToggle, onClose }) {
  return (
    <Link
      to={to} onClick={onClose}
      data-testid={`farmers-nav-more-${testidOf(label)}`}
      className="group flex items-center gap-2 rounded-xl px-2 py-1.5 text-sm text-[#3A3830] hover:bg-[#FBF2E1] hover:text-[#1B4332]"
    >
      <Icon size={13} weight="fill" className="text-[color:var(--np-brand,#B85F2A)] shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      <button
        onClick={(e) => onPinToggle(to, e)}
        title={pinned || isPinned ? "Unpin" : "Pin to top"}
        data-testid={`farmers-nav-pin-${testidOf(label)}`}
        className={`opacity-0 group-hover:opacity-100 text-[#B85F2A] hover:text-[#1B4332] transition ${
          pinned || isPinned ? "opacity-100" : ""
        }`}
      >
        <Sparkle size={12} weight={pinned || isPinned ? "fill" : "regular"} />
      </button>
    </Link>
  );
}
